import { getSupabase } from './supabase/client';
import { requireUserId } from './supabase/auth';
import { openExternalSafely } from '../security/policies';
import type { BillingInterval, BillingPlanDTO, BillingStatusDTO, CouponRedeemDTO, PaidPlan } from '../shared/types';
import { t } from './language';
import { ApiError, apiBaseUrl, apiRequest } from './api';

const PAID_PLAN_IDS: readonly PaidPlan[] = ['pro', 'plus'];

/**
 * Abonelik (iyzico). Ödeme işlemleri web/ uygulamasının API'sinde — iyzico
 * gizli anahtarı masaüstüne konamaz. Masaüstü:
 *  - kendi abonelik satırını Supabase'ten (RLS) okur,
 *  - plan bilgisini ve ödeme başlatmayı TalkNote API'sine sorar (bkz. api.ts),
 *  - ödeme sayfasını sistem tarayıcısında açar.
 */

export async function getBillingStatus(): Promise<BillingStatusDTO> {
  const status: BillingStatusDTO = { configured: Boolean(apiBaseUrl()) };

  // Yöneticinin tüm satırları okuma politikası var — kendi satırıyla sınırla. "*": sıradaki
  // plan kolonları (migration 0053) henüz yoksa da okunsun.
  const { data } = await getSupabase()
    .from('subscriptions')
    .select('*')
    .eq('user_id', await requireUserId())
    .maybeSingle();
  if (data) {
    status.subscription = {
      status: data.status,
      startedAtISO: data.started_at ?? undefined,
      currentPeriodEndISO: data.current_period_end ?? undefined,
      provider: data.provider ?? undefined,
      planReference: data.pricing_plan_reference_code ?? undefined,
      scheduledReference: data.scheduled_reference ?? undefined,
      scheduledPeriodEndISO: data.scheduled_period_end ?? undefined,
    };
  }

  if (status.configured) {
    try {
      const { plan, plans } = await apiRequest<{ plan: BillingPlanDTO | null; plans?: BillingPlanDTO[] }>('GET', '/api/billing/plan');
      status.plan = plan ?? undefined;
      status.plans = plans ?? (plan ? [plan] : []);
    } catch (err) {
      status.planError = (err as Error).message;
    }
  }
  return status;
}

/**
 * Ödeme oturumu açar; ödeme sayfasının adresini döndürür. Fatura bilgileri,
 * sözleşme onayı ve kart o sayfada girilir (bkz. web/app/billing/checkout).
 */
export async function requestCheckoutUrl(input: unknown): Promise<string> {
  const value = (input ?? {}) as { plan?: unknown; interval?: unknown; test?: unknown };
  const plan: PaidPlan = PAID_PLAN_IDS.includes(value.plan as PaidPlan) ? (value.plan as PaidPlan) : 'pro';
  const interval: BillingInterval = value.interval === 'yearly' ? 'yearly' : 'monthly';
  const { url } = await apiRequest<{ url: string }>('POST', '/api/billing/session', {
    body: { plan, interval, ...(value.test === true ? { test: true } : {}) },
  });
  if (!url) throw new Error(t('Ödeme sayfası açılamadı.', 'Could not open the payment page.'));
  return url;
}

/** Ödeme sayfasını sistem tarayıcısında açar. */
export async function startBillingCheckout(input: unknown): Promise<void> {
  const url = await requestCheckoutUrl(input);
  if (!openExternalSafely(url)) throw new Error(t('Ödeme sayfası açılamadı.', 'Could not open the payment page.'));
}

export async function cancelBillingSubscription(): Promise<void> {
  await apiRequest('POST', '/api/billing/cancel');
}

/** Aboneliği iyzico'dan tazeler (yenileme/iptal webhook'u yokken). */
export async function refreshBillingSubscription(): Promise<void> {
  await apiRequest('POST', '/api/billing/refresh');
}

/** Sunucunun kupon hata kodları → arayüz dilinde mesaj (web lib/billing/coupons.ts). */
function couponErrorMessage(reason: string | undefined): string | null {
  switch (reason) {
    case 'not_found':
      return t('Kupon kodu geçersiz.', 'This coupon code is not valid.');
    case 'expired':
      return t('Bu kuponun süresi dolmuş.', 'This coupon has expired.');
    case 'exhausted':
      return t('Bu kuponun kullanım hakkı dolmuş.', 'This coupon has been fully redeemed.');
    case 'already_redeemed':
      return t('Bu kuponu zaten kullandın.', 'You have already used this coupon.');
    case 'managed_plan':
      return t('Hesabına özel bir plan tanımlı; kupon uygulanamıyor.', 'Your account has a custom plan, so the coupon cannot be applied.');
    case 'plan_not_better':
      return t('Mevcut planın bu kuponun verdiği plandan zaten yüksek.', 'Your current plan is already higher than what this coupon gives.');
    case 'has_subscription':
      return t('Aktif bir aboneliğin varken indirim kuponu kullanılamıyor.', 'Discount coupons cannot be used while you have an active subscription.');
    case 'discount_pending':
      return t('Henüz kullanılmamış bir indirim kuponun var.', 'You already have an unused discount coupon.');
    default:
      return null;
  }
}

/** Kupon kodunu kullanır: plan hediyesi hemen geçerli, indirim bir sonraki ödemede. */
export async function redeemCoupon(code: unknown): Promise<CouponRedeemDTO> {
  const value = typeof code === 'string' ? code.trim().toUpperCase().slice(0, 40) : '';
  if (value.length < 3) throw new Error(t('Kupon kodunu gir.', 'Enter a coupon code.'));
  try {
    const { coupon } = await apiRequest<{
      coupon: { kind: CouponRedeemDTO['kind']; plan: CouponRedeemDTO['plan']; until: string | null; interval: BillingInterval | null; percentOff: number | null };
    }>('POST', '/api/v1/coupons/redeem', { body: { code: value } });
    return {
      kind: coupon.kind,
      plan: coupon.plan,
      untilISO: coupon.until ?? undefined,
      interval: coupon.interval ?? undefined,
      percentOff: coupon.percentOff ?? undefined,
    };
  } catch (err) {
    const message = err instanceof ApiError ? couponErrorMessage(err.reason) : null;
    throw message ? new Error(message) : err;
  }
}
